import type { AccessModule, Plan } from "./enums";

/**
 * Free-plan item caps. Same ladder as `FreeTier.limit(for:)` on iOS (2.1.1) — keep
 * them identical. Account-level ceilings (active weddings, storage) live in
 * plan-limits.ts; `extraWedding` here is the iOS name for "one wedding on free".
 * Caps apply to NEW inserts only — existing rows are always readable,
 * editable, deletable and exportable. Server is the gate; the client copy
 * exists only so the upgrade prompt can appear before the round-trip.
 */
export const PREMIUM_FEATURE = [
  "guests", "checklist", "budget", "vendors", "timeline", "shopping",
  "notes", "inspiration", "registry", "seating", "extraWedding", "collaborators",
] as const;
export type PremiumFeature = (typeof PREMIUM_FEATURE)[number];

/** The ten per-section caps — every PremiumFeature that is a module, not an account ceiling. */
export const ITEM_FEATURES = [
  "guests", "checklist", "budget", "vendors", "timeline", "shopping", "notes", "inspiration", "registry", "seating",
] as const;
export type ItemFeature = (typeof ITEM_FEATURES)[number];

export const FREE_LIMITS: Record<PremiumFeature, number> = {
  guests: 20,
  checklist: 40,
  budget: 25,
  vendors: 5,
  timeline: 8,
  shopping: 10,
  notes: 3,
  inspiration: 6,
  registry: 6,
  seating: 2,
  extraWedding: 1,
  collaborators: 1,
};

export function featureForModule(m: AccessModule): PremiumFeature | null {
  switch (m) {
    case "guests": case "checklist": case "budget": case "vendors": case "timeline":
    case "shopping": case "notes": case "inspiration": case "registry": case "seating":
      return m;
    default:
      return null;
  }
}

/** Remaining headroom for `plan`; `Infinity` when unlimited (every paid tier — bounded by storage + fair use, see plan-limits.ts). */
export function remaining(plan: Plan, feature: PremiumFeature, currentCount: number): number {
  if (plan !== "free") return Number.POSITIVE_INFINITY;
  return Math.max(0, FREE_LIMITS[feature] - currentCount);
}

/** True when inserting `adding` more rows would exceed the cap. */
export function blocksInsert(plan: Plan, feature: PremiumFeature, currentCount: number, adding = 1): boolean {
  return remaining(plan, feature, currentCount) < adding;
}
